import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import {
  getMergedCompetencies,
  getNumberOfMatchingCompetencies
} from 'lib/competencies';
import { LinkButton, ProgressBar, TitleWithIcon } from 'components/common';
import { Box, BoxEmpty, BoxRow } from 'components/common/box';
import { ProfileCompetencyProfileDetails } from '.';

import type {
  GetUserPinnedCompetencyProfilesRes,
  GetUsersRes
} from 'shared-types/responses';

interface Props {
  isUserPage?: boolean;
}

const ProfileSummaryCompetencyProfiles = ({ isUserPage }: Props) => {
  const {
    language,
    messages,
    pageState: { user },
    theme,
    user: loggedInUser
  } = useReduxState();

  const [detailsProfileId, setDetailsProfileId] = useState<number | null>(null);

  const { data: userData, loading: userLoading } = useApiData<GetUsersRes>({
    onlyIf: Boolean(user?.id),
    url: `/admin/users?id=${user?.id}`
  });

  const {
    data: pinnedCompetencyProfilesData,
    loading: pinnedCompetencyProfilesLoading
  } = useApiData<GetUserPinnedCompetencyProfilesRes>({
    onlyIf: Boolean(user?.id),
    url: `/admin/users/${user?.id}/pinned-competency-profiles`
  });

  const loading = userLoading || pinnedCompetencyProfilesLoading;

  const mergedCompetencies = getMergedCompetencies(
    userData?.users?.[0]?.competencies || []
  );
  const competencyProfiles =
    pinnedCompetencyProfilesData?.competencyProfiles || [];
  const detailsCompetencyProfile = competencyProfiles.find(
    (competencyProfile) => competencyProfile.id === detailsProfileId
  );

  return (
    <>
      <Box
        headerContent={
          <LinkButton
            category="lighter"
            href={
              isUserPage
                ? `/users/${user?.id}/competency-profiles`
                : '/profile/competency-profiles'
            }
            size="header"
            text={messages.commonShowAll}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        title={messages.competencyProfilesPinnedTitle}
        {...(!loading ? { titleCount: competencyProfiles.length } : {})}
      >
        {competencyProfiles.length ? (
          competencyProfiles.map((competencyProfile) => {
            const numberOfMatched = getNumberOfMatchingCompetencies({
              competencies: competencyProfile.competencies,
              userCompetencies: mergedCompetencies
            });
            const numberOfTotal = competencyProfile.competencies?.length || 0;
            const description =
              competencyProfile.descriptionLocales?.[language] ||
              competencyProfile.description;

            return (
              <BoxRow key={competencyProfile.id}>
                <div className="row-sm row-center">
                  <div className="col">
                    <TitleWithIcon
                      icon="address-card"
                      onClick={() => setDetailsProfileId(competencyProfile.id)}
                      style={description ? undefined : { alignItems: 'center' }}
                      title={
                        competencyProfile.titleLocales?.[language] ||
                        competencyProfile.title
                      }
                    >
                      {description}
                    </TitleWithIcon>
                  </div>
                  <div className="col-min">
                    <ProgressBar
                      color={theme.successColor}
                      label={getMessageWithPlaceholders({
                        message: messages.competenciesNumberOfCompetencies,
                        placeholders: {
                          numberOfCompetencies: `${numberOfMatched} / ${numberOfTotal}`
                        }
                      })}
                      labelStyle={{ marginBottom: 3 }}
                      progress={
                        numberOfTotal ? numberOfMatched / numberOfTotal : 0
                      }
                      progressStyle={{ width: 200 }}
                    />
                  </div>
                </div>
              </BoxRow>
            );
          })
        ) : (
          <BoxEmpty
            align="center"
            text={messages.competencyProfilesPinnedEmpty}
          />
        )}
      </Box>

      <ProfileCompetencyProfileDetails
        competencyProfile={detailsCompetencyProfile}
        includeCompetencyProfileLink={
          Boolean(loggedInUser?.roles.includes('admin')) &&
          Boolean(
            detailsCompetencyProfile?.canManage ||
              detailsCompetencyProfile?.isVisible
          )
        }
        onHide={() => setDetailsProfileId(null)}
        userCompetencies={mergedCompetencies}
      />
    </>
  );
};

export default ProfileSummaryCompetencyProfiles;
